import React, { useState } from 'react';
import { TaskAuditLog } from '../Task.interface';
import { ListItem, Typography } from '@material-ui/core';
import { formatDate } from '../../../common/formatter';
import styled from 'styled-components';
import {
  getProductEntityIdPrefix,
  getProductEntityName
} from '../../products/Product.config';
import { getProductEntityPrefix } from '../../products/Product.hooks';
import {
  hazardRisk,
  hazardProbability,
  hazardSeverity
} from '../../hazards/Hazard.interface';

const StyledListItem = styled(ListItem)`
  flex-direction: column;
  align-items: flex-start;

  &.collapse {
    .field-change {
      flex-direction: row;
      overflow: hidden;
      white-space: nowrap;
      width: 100%;
    }
    ins {
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }
  .field-change {
    display: flex;
    flex-direction: column;
    /* align-items: center; */
    .field {
      margin-right: 8px;
    }
  }
  del {
    display: inline;
    background: #ffbdad;
    padding: 3px 2px;
    font-size: 11px;
  }
  ins {
    display: inline;
    background: #abf5d3;
    padding: 3px 2px;
    text-decoration: none;
    font-size: 11px;
  }
`;

const TaskAuditListItem: React.FC<{ log: TaskAuditLog }> = ({ log }) => {
  const [collapse, setCollapse] = useState(true);
  let classes = ['list-item'];
  if (collapse) classes.push('collapse');
  return (
    <StyledListItem
      className={classes.join(' ')}
      button
      divider
      key={log.id}
      onClick={() => setCollapse(!collapse)}
    >
      <Typography variant='overline'>
        {formatDate(log.created)} ({log.createdBy.firstName}{' '}
        {log.createdBy.lastName})
      </Typography>
      {Object.keys(log.newValue).map(key => {
        if (key === 'id') return null;
        let fieldLabel = translateKey(key);
        fieldLabel = fieldLabel.replace(/([A-Z])/g, ' $1');
        fieldLabel = fieldLabel.charAt(0).toUpperCase() + fieldLabel.slice(1);
        let oldValue = renderValue(key, log.oldValue[key]);
        let newValue = renderValue(key, log.newValue[key]);
        if (collapse && typeof newValue === 'string')
          newValue = newValue.replace(/(<([^>]+)>)/gi, '');
        return (
          <div className='field-change' key={key}>
            <Typography variant='subtitle2' className='field'>
              {fieldLabel}
            </Typography>
            {!collapse && (
              <del dangerouslySetInnerHTML={{ __html: oldValue }} />
            )}
            {/* show deleted value if newValue is cleared */}
            {collapse && !newValue ? (
              <del dangerouslySetInnerHTML={{ __html: oldValue }} />
            ) : (
              <ins dangerouslySetInnerHTML={{ __html: newValue }} />
            )}
          </div>
        );
      })}
    </StyledListItem>
  );
};

export default TaskAuditListItem;

const translateKey = (key: string) => {
  switch (key) {
    case 'userNeeds':
      return getProductEntityName('userNeed');
    case 'designInputs':
      return getProductEntityName('designInput');
    case 'designOutputs':
      return getProductEntityName('designOutput');
    case 'verifications':
      return getProductEntityName('verification');
    case 'validations':
      return getProductEntityName('validation');
    case 'designSpecs':
      return getProductEntityName('designSpec');
    case 'initialHarm':
      return 'Initial likelihood of harm';
    case 'residualHarm':
      return 'Residual likelihood of harm';
    default:
      return key;
  }
};

const renderValue = (key: string, value: any): string => {
  if (value === null) return '';
  if (Array.isArray(value)) {
    return value.map(value => renderValue(key, value)).join(', ');
  }
  switch (key) {
    case 'userNeeds':
      return getProductEntityIdPrefix('userNeed') + '-' + value.id;
    case 'designInputs':
      return getProductEntityIdPrefix('designInput') + '-' + value.id;
    case 'designOutputs':
      return getProductEntityIdPrefix('designOutput') + '-' + value.id;
    case 'verifications':
      return getProductEntityIdPrefix('verification') + '-' + value.id;
    case 'validations':
      return getProductEntityIdPrefix('validation') + '-' + value.id;
    case 'designSpecs':
      return getProductEntityIdPrefix('designSpec') + '-' + value.id;
    case 'causes':
      return 'CAUSE-' + value.id;
    case 'controls':
      return 'CONTROLS-' + value.id;
    case 'actions':
      return 'ACTION-' + value.id;
    case 'initialSeverity':
      return hazardSeverity.find(s => s.value === value)?.label || value;
    case 'initialProbability':
      return hazardProbability.find(s => s.value === value)?.label || value;
    case 'initialRisk':
      return hazardRisk.find(s => s.value === value)?.label || value;
    case 'residualSeverity':
      return hazardSeverity.find(s => s.value === value)?.label || value;
    case 'residualProbability':
      return hazardProbability.find(s => s.value === value)?.label || value;
    case 'residualRisk':
      return hazardRisk.find(s => s.value === value)?.label || value;
    case 'initialHarm':
      return hazardProbability.find(s => s.value === value)?.label || value;
    case 'residualHarm':
      return hazardProbability.find(s => s.value === value)?.label || value;
  }
  if (value instanceof Date) {
    return formatDate(value);
  }
  switch (typeof value) {
    case 'object': {
      const key = [
        'name',
        'title',
        'firstName',
        'lastName',
        'description',
        'stepNo'
      ].find(key => Object.keys(value).includes(key));
      if (key === 'firstName') {
        return `${value['firstName']} ${value['lastName']}`;
      } else if (key) {
        return value[key];
      } else {
        return JSON.stringify(value);
      }
    }
    default:
      return value;
  }
};
